import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  SupabaseUserSettingsRepository,
  type DatabaseUserSettings,
} from "@/repositories/supabase/UserSettingsRepository";

const userSettingsRepo = new SupabaseUserSettingsRepository();

export const useUserSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: userSettings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userSettings", user?.id],
    queryFn: () => userSettingsRepo.get(),
    enabled: !!user,
  });

  const updateSettings = useMutation({
    mutationFn: (
      updates: Partial<
        Pick<
          DatabaseUserSettings,
          | "theme"
          | "currency"
          | "preferred_rate_source"
          | "show_original_currency"
          | "fx_rate_staleness_threshold"
          | "ynab_personal_access_token"
        >
      >,
    ) => userSettingsRepo.update(updates as any),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["userSettings", user?.id] });
      const previous = queryClient.getQueryData<DatabaseUserSettings | null>([
        "userSettings",
        user?.id,
      ]);

      const next = {
        ...(previous ?? {
          id: "",
          user_id: user?.id ?? "",
          theme: "system",
          currency: "USD",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          preferred_rate_source: "live",
          show_original_currency: false,
          fx_rate_staleness_threshold: "6 hours" as any,
          ynab_personal_access_token: null,
        }),
        ...updates,
        updated_at: new Date().toISOString(),
      } as DatabaseUserSettings;

      // Optimistically update cache so the context effect applies the theme immediately
      queryClient.setQueryData(["userSettings", user?.id], next);

      return { previous };
    },
    onError: (_err, _vars, ctx: any) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(["userSettings", user?.id], ctx.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["userSettings", user?.id],
      });
    },
  });

  const updateDisplayCurrency = (currency: string) => {
    updateSettings.mutate({ currency });
  };

  const updateTheme = (theme: string) => {
    updateSettings.mutate({ theme });
  };

  const updatePreferredRateSource = (preferred_rate_source: string | null) => {
    updateSettings.mutate({ preferred_rate_source } as any);
  };

  const updateShowOriginalCurrency = (show_original_currency: boolean) => {
    updateSettings.mutate({ show_original_currency } as any);
  };

  const updateFxStalenessHours = (hours: number) => {
    // Store as a Postgres interval literal
    updateSettings.mutate({
      fx_rate_staleness_threshold: `${hours} hours`,
    } as any);
  };

  const updateYnabPersonalAccessToken = (token: string) => {
    console.log(
      "[useUserSettings] updateYnabPersonalAccessToken called with token:",
      token ? "***TOKEN_PRESENT***" : "EMPTY",
    );
    updateSettings.mutate({ ynab_personal_access_token: token });
  };

  return {
    userSettings,
    isLoading,
    error,
    updateSettings,
    updateDisplayCurrency,
    updateTheme,
    updatePreferredRateSource,
    updateShowOriginalCurrency,
    updateFxStalenessHours,
    updateYnabPersonalAccessToken,
  };
};
